import React, { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  Clock,
  MapPin,
  Trash2,
  Edit2,
  Palmtree,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import api from '../services/api';

const DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Tipos de evento con colores
const EVENT_TYPES = {
  evento: { label: 'Evento', color: 'blue', bgClass: 'bg-blue-500', textClass: 'text-blue-600', lightBg: 'bg-blue-50', border: 'border-blue-200' },
  reunion: { label: 'Reunión', color: 'purple', bgClass: 'bg-purple-500', textClass: 'text-purple-600', lightBg: 'bg-purple-50', border: 'border-purple-200' },
  tarea: { label: 'Tarea', color: 'green', bgClass: 'bg-green-500', textClass: 'text-green-600', lightBg: 'bg-green-50', border: 'border-green-200' },
  vacaciones: { label: 'Vacaciones', color: 'orange', bgClass: 'bg-orange-500', textClass: 'text-orange-600', lightBg: 'bg-orange-50', border: 'border-orange-200' },
  ausencia: { label: 'Ausencia', color: 'red', bgClass: 'bg-red-500', textClass: 'text-red-600', lightBg: 'bg-red-50', border: 'border-red-200' },
  recordatorio: { label: 'Recordatorio', color: 'yellow', bgClass: 'bg-yellow-500', textClass: 'text-yellow-600', lightBg: 'bg-yellow-50', border: 'border-yellow-200' },
};

// Estados de aprobacion para vacaciones
const APPROVAL_STATUS = {
  pending: { label: 'Pendiente', icon: AlertCircle, color: 'text-yellow-600' },
  approved: { label: 'Aprobado', icon: CheckCircle, color: 'text-green-600' },
  rejected: { label: 'Rechazado', icon: XCircle, color: 'text-red-600' },
};

export default function ChatCalendar({ department, user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [filterType, setFilterType] = useState(null); // Filtro por tipo de evento
  const [isCollapsed, setIsCollapsed] = useState(false); // Estado de colapso del calendario
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    end_date: '',
    time: '',
    duration: '60',
    location: '',
    reminder: '30',
    type: 'evento'
  });

  // Verificar si usuario puede aprobar vacaciones (admin/superadmin)
  const canApproveVacations = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    if (department?.id) {
      loadEvents();
    }
  }, [department?.id, currentDate]);

  const loadEvents = async () => {
    if (!department?.id) return;
    try {
      const response = await api.get(`/api/events/department/${department.id}`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.log('Error cargando eventos:', error);
      setEvents([]);
    }
  };

  // Obtener dias del mes
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days = [];

    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i)
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }

    return days;
  };

  const days = getDaysInMonth(currentDate);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.date);
      const eventEnd = event.end_date ? new Date(event.end_date) : eventStart;
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      eventStart.setHours(0, 0, 0, 0);
      eventEnd.setHours(0, 0, 0, 0);
      return checkDate >= eventStart && checkDate <= eventEnd;
    });
  };

  const hasEvents = (date) => {
    return getEventsForDate(date).length > 0;
  };

  // Obtener color predominante de eventos del dia
  const getDayEventColor = (date) => {
    const dayEvents = getEventsForDate(date);
    if (dayEvents.length === 0) return null;

    // Prioridad: vacaciones > ausencia > reunion > tarea > evento
    const priority = ['vacaciones', 'ausencia', 'reunion', 'tarea', 'evento', 'recordatorio'];
    for (const type of priority) {
      const found = dayEvents.find(e => e.type === type);
      if (found) {
        // Si es vacaciones pendiente, mostrar diferente
        if (type === 'vacaciones' && found.approval_status === 'pending') {
          return 'bg-orange-200 border-orange-400';
        }
        if (type === 'vacaciones' && found.approval_status === 'rejected') {
          return 'bg-red-100 border-red-300';
        }
        return EVENT_TYPES[type]?.lightBg + ' ' + EVENT_TYPES[type]?.border || 'bg-blue-50 border-blue-200';
      }
    }
    return 'bg-blue-50 border-blue-200';
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
  };

  const openNewEventModal = () => {
    setEditingEvent(null);
    const dateStr = selectedDate.toISOString().split('T')[0];
    setEventForm({
      title: '',
      description: '',
      date: dateStr,
      end_date: '',
      time: '10:00',
      duration: '60',
      location: '',
      reminder: '30',
      type: 'evento'
    });
    setShowEventModal(true);
  };

  const openEditEventModal = (event) => {
    setEditingEvent(event);
    const eventDate = new Date(event.date);
    setEventForm({
      title: event.title,
      description: event.description || '',
      date: eventDate.toISOString().split('T')[0],
      end_date: event.end_date ? new Date(event.end_date).toISOString().split('T')[0] : '',
      time: event.time || '10:00',
      duration: event.duration || '60',
      location: event.location || '',
      reminder: event.reminder || '30',
      type: event.type || 'evento'
    });
    setShowEventModal(true);
  };

  const handleSaveEvent = async () => {
    try {
      const eventData = {
        ...eventForm,
        department_id: department.id,
        created_by: user.id,
        created_by_name: user.name,
        // Si es vacaciones/ausencia, requiere aprobacion
        approval_status: (eventForm.type === 'vacaciones' || eventForm.type === 'ausencia') ? 'pending' : null
      };

      if (editingEvent) {
        await api.put(`/api/events/${editingEvent.id}`, eventData);
      } else {
        await api.post('/api/events', eventData);
      }

      setShowEventModal(false);
      loadEvents();
    } catch (error) {
      console.error('Error guardando evento:', error);
      // Simular guardado local si no hay API
      const newEvent = {
        id: `event-${Date.now()}`,
        ...eventForm,
        department_id: department.id,
        created_by: user.id,
        created_by_name: user.name,
        approval_status: (eventForm.type === 'vacaciones' || eventForm.type === 'ausencia') ? 'pending' : null,
        created_at: new Date().toISOString()
      };

      if (editingEvent) {
        setEvents(prev => prev.map(e => e.id === editingEvent.id ? { ...e, ...eventForm } : e));
      } else {
        setEvents(prev => [...prev, newEvent]);
      }
      setShowEventModal(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;

    try {
      await api.delete(`/api/events/${eventId}`);
      loadEvents();
    } catch (error) {
      console.error('Error eliminando evento:', error);
      setEvents(prev => prev.filter(e => e.id !== eventId));
    }
  };

  // Aprobar o rechazar vacaciones
  const handleApprovalAction = async (eventId, action) => {
    try {
      await api.put(`/api/events/${eventId}`, { approval_status: action });
      loadEvents();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      setEvents(prev => prev.map(e =>
        e.id === eventId ? { ...e, approval_status: action } : e
      ));
    }
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 bg-white rounded-lg p-2 shadow-sm border">
        <span className="text-sm font-medium text-gray-700 flex items-center">
          <Calendar className="w-4 h-4 mr-1.5 text-blue-500" />
          Calendario
          {isCollapsed && events.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
              {events.length}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {!isCollapsed && (
            <button
              onClick={openNewEventModal}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Nuevo evento"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={isCollapsed ? "Expandir calendario" : "Colapsar calendario"}
          >
            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Contenido del calendario - con transición de colapso */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
        }`}
      >

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded text-gray-500">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-gray-700">
          {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded text-gray-500">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg p-2 border shadow-sm">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map(day => (
            <div key={day} className="text-center text-xs font-semibold text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Days - 6 rows of 7 days */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayObj, idx) => {
            const dayColor = getDayEventColor(dayObj.date);
            const dayEvents = getEventsForDate(dayObj.date);

            return (
              <button
                key={idx}
                onClick={() => handleDayClick(dayObj.date)}
                className={`
                  relative flex flex-col items-center justify-center text-xs rounded-md h-8 w-full
                  transition-all duration-150 border
                  ${!dayObj.isCurrentMonth ? 'text-gray-300 border-transparent' : 'text-gray-700'}
                  ${isToday(dayObj.date) ? 'bg-blue-500 text-white font-bold border-blue-600 shadow-sm' : ''}
                  ${isSelected(dayObj.date) && !isToday(dayObj.date) ? 'ring-2 ring-blue-400 border-blue-300' : ''}
                  ${!isToday(dayObj.date) && dayColor ? dayColor + ' border' : 'border-transparent'}
                  ${!isToday(dayObj.date) && !isSelected(dayObj.date) && !dayColor ? 'hover:bg-gray-50 hover:border-gray-200' : ''}
                `}
                title={dayEvents.length > 0 ? dayEvents.map(e => e.title).join(', ') : ''}
              >
                <span className="font-medium">{dayObj.day}</span>
                {dayEvents.length > 0 && !isToday(dayObj.date) && (
                  <div className="flex gap-0.5 mt-0.5">
                    {dayEvents.slice(0, 3).map((ev, i) => (
                      <span
                        key={i}
                        className={`w-1 h-1 rounded-full ${EVENT_TYPES[ev.type]?.bgClass || 'bg-blue-500'}`}
                      />
                    ))}
                  </div>
                )}
                {dayEvents.length > 0 && isToday(dayObj.date) && (
                  <div className="flex gap-0.5 mt-0.5">
                    {dayEvents.slice(0, 3).map((_, i) => (
                      <span key={i} className="w-1 h-1 rounded-full bg-white" />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Leyenda de colores - Pulsable para filtrar */}
      <div className="flex flex-wrap gap-1 mt-2">
        {Object.entries(EVENT_TYPES).map(([key, type]) => {
          const count = events.filter(e => e.type === key).length;
          return (
            <button
              key={key}
              onClick={() => setFilterType(filterType === key ? null : key)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all ${
                filterType === key
                  ? `${type.bgClass} text-white`
                  : count > 0
                    ? `${type.lightBg} ${type.textClass} hover:ring-1 ring-${type.color}-300`
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${filterType === key ? 'bg-white' : type.bgClass}`} />
              {type.label}
              {count > 0 && <span className="ml-0.5 font-medium">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Eventos filtrados por tipo */}
      {filterType && (
        <div className="mt-3 p-2 rounded-lg border" style={{ backgroundColor: EVENT_TYPES[filterType]?.lightBg?.replace('bg-', '') }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold flex items-center gap-1" style={{ color: EVENT_TYPES[filterType]?.textClass?.replace('text-', '') }}>
              <span className={`w-2 h-2 rounded-full ${EVENT_TYPES[filterType]?.bgClass}`} />
              {EVENT_TYPES[filterType]?.label} ({events.filter(e => e.type === filterType).length})
            </p>
            <button
              onClick={() => setFilterType(null)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {events.filter(e => e.type === filterType).length === 0 ? (
              <p className="text-xs text-gray-500">No hay {EVENT_TYPES[filterType]?.label.toLowerCase()}</p>
            ) : (
              events.filter(e => e.type === filterType).map(event => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-1.5 bg-white rounded text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{event.title}</p>
                    <p className="text-gray-500">
                      {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                      {event.end_date && event.end_date !== event.date && (
                        <> - {new Date(event.end_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</>
                      )}
                      {event.created_by_name && <> • {event.created_by_name}</>}
                    </p>
                  </div>
                  {event.approval_status && (
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                      event.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                      event.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {APPROVAL_STATUS[event.approval_status]?.label}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Selected Date Events */}
      {!filterType && (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-600">
            {selectedDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        <div className="space-y-2 max-h-40 overflow-y-auto">
          {selectedDateEvents.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
              <p className="text-xs text-gray-500">Sin eventos</p>
              <button
                onClick={openNewEventModal}
                className="mt-1.5 text-xs text-blue-600 hover:underline font-medium"
              >
                + Crear evento
              </button>
            </div>
          ) : (
            selectedDateEvents.map(event => {
              const eventType = EVENT_TYPES[event.type] || EVENT_TYPES.evento;
              const ApprovalIcon = event.approval_status ? APPROVAL_STATUS[event.approval_status]?.icon : null;

              return (
                <div
                  key={event.id}
                  className={`rounded-lg p-2 border-l-4 ${eventType.lightBg} ${eventType.border} border-l-${eventType.color}-500`}
                  style={{ borderLeftColor: `var(--${eventType.color}-500, #3b82f6)` }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {event.type === 'vacaciones' && <Palmtree className="w-3 h-3 text-orange-500" />}
                        <p className={`text-xs font-semibold ${eventType.textClass}`}>{event.title}</p>
                      </div>

                      {event.end_date && event.end_date !== event.date && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {new Date(event.end_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </p>
                      )}

                      {event.time && (
                        <p className="text-xs text-gray-500 flex items-center mt-0.5">
                          <Clock className="w-3 h-3 mr-1" />
                          {event.time}
                        </p>
                      )}

                      {event.location && (
                        <p className="text-xs text-gray-500 flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {event.location}
                        </p>
                      )}

                      {/* Estado de aprobacion para vacaciones */}
                      {event.approval_status && (
                        <div className="flex items-center gap-1 mt-1">
                          {ApprovalIcon && <ApprovalIcon className={`w-3 h-3 ${APPROVAL_STATUS[event.approval_status].color}`} />}
                          <span className={`text-xs ${APPROVAL_STATUS[event.approval_status].color}`}>
                            {APPROVAL_STATUS[event.approval_status].label}
                          </span>
                        </div>
                      )}

                      {/* Botones de aprobacion (solo para admin) */}
                      {event.approval_status === 'pending' && canApproveVacations && event.created_by !== user.id && (
                        <div className="flex gap-1 mt-1.5">
                          <button
                            onClick={() => handleApprovalAction(event.id, 'approved')}
                            className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Aprobar
                          </button>
                          <button
                            onClick={() => handleApprovalAction(event.id, 'rejected')}
                            className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Rechazar
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-0.5 ml-1">
                      <button
                        onClick={() => openEditEventModal(event)}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      )}
    </div>
    {/* Fin del contenido colapsable */}

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                {editingEvent ? 'Editar evento' : 'Nuevo evento'}
              </h3>
              <button
                onClick={() => setShowEventModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Tipo de evento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de evento
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(EVENT_TYPES).map(([key, type]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setEventForm({ ...eventForm, type: key })}
                      className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                        eventForm.type === key
                          ? `${type.lightBg} ${type.border} ${type.textClass} ring-2 ring-offset-1`
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                      style={eventForm.type === key ? { ringColor: `var(--${type.color}-400)` } : {}}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full ${type.bgClass} mr-1`} />
                      {type.label}
                    </button>
                  ))}
                </div>
                {(eventForm.type === 'vacaciones' || eventForm.type === 'ausencia') && (
                  <p className="text-xs text-orange-600 mt-2 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Requiere aprobación de un administrador
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titulo *
                </label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={eventForm.type === 'vacaciones' ? 'Ej: Vacaciones de verano' : 'Ej: Reunion con cliente'}
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha inicio *
                  </label>
                  <input
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha fin <span className="text-gray-400 text-xs">(opcional)</span>
                  </label>
                  <input
                    type="date"
                    value={eventForm.end_date}
                    onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                    min={eventForm.date}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {eventForm.type !== 'vacaciones' && eventForm.type !== 'ausencia' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora
                  </label>
                  <input
                    type="time"
                    value={eventForm.time}
                    onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {eventForm.type !== 'vacaciones' && eventForm.type !== 'ausencia' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duracion
                    </label>
                    <select
                      value={eventForm.duration}
                      onChange={(e) => setEventForm({ ...eventForm, duration: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="15">15 min</option>
                      <option value="30">30 min</option>
                      <option value="45">45 min</option>
                      <option value="60">1 hora</option>
                      <option value="90">1.5 horas</option>
                      <option value="120">2 horas</option>
                      <option value="180">3 horas</option>
                      <option value="240">4 horas</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recordatorio
                    </label>
                    <select
                      value={eventForm.reminder}
                      onChange={(e) => setEventForm({ ...eventForm, reminder: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="0">Sin recordatorio</option>
                      <option value="5">5 min antes</option>
                      <option value="15">15 min antes</option>
                      <option value="30">30 min antes</option>
                      <option value="60">1 hora antes</option>
                      <option value="1440">1 dia antes</option>
                    </select>
                  </div>
                </div>
              )}

              {eventForm.type !== 'vacaciones' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicacion
                  </label>
                  <input
                    type="text"
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Sala de reuniones, Online..."
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {eventForm.type === 'vacaciones' ? 'Motivo / Notas' : 'Descripcion'}
                </label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder={eventForm.type === 'vacaciones' ? 'Motivo de las vacaciones...' : 'Notas adicionales...'}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEvent}
                disabled={!eventForm.title || !eventForm.date}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  eventForm.type === 'vacaciones' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {editingEvent ? 'Guardar' : eventForm.type === 'vacaciones' ? 'Solicitar' : 'Crear evento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
